import React from 'react';
import { StyleSheet, View, TouchableOpacity, Platform, Linking } from 'react-native';
import { ODSText } from '../../atoms/ODSText';
import ODSButton from '../../atoms/ODSButton';
import { ODSIcon } from '../../atoms/ODSIcon';
import { getTokens } from '../../../design-tokens/color-tokens';
import RentReviewSkeleton from './RentReviewSkeleton';

export { RentReviewSkeleton };

export interface DocumentItem {
  id?: string | number;
  title: string;
  date: string;
  status?: string;
  url?: string;
  isSigned?: boolean;
  rentReviewId?: string | number;
}

export interface RentReviewDetailCardProps {
  restStyle?: any;
  title?: string;
  description?: string;
  newRentAmount?: string;
  badgeText?: string;
  documents?: DocumentItem[];
  statusTagText?: string;
  infoMessage?: string;
  rejectButtonText?: string;
  signButtonText?: string;
  showButtons?: boolean;
  showRejectButton?: boolean;
  isEmpty?: boolean;
  emptyMessage?: string;
  onRejectOffer?: () => void;
  onSignAgreement?: () => void;
  onDownloadDoc?: (doc: DocumentItem) => void;
  onContactAgent?: () => void;
}

const formatText = (str?: string) => {
  if (!str) return '';
  const formatted = String(str).replace(/_/g, ' ');
  if (str.includes('_') || str === str.toLowerCase()) {
    return formatted.replace(/\b\w/g, (c) => c.toUpperCase());
  }
  return formatted;
};

const RentReviewDetailCard: React.FC<RentReviewDetailCardProps> = ({
  restStyle,
  title = 'Rent Review Request',
  description = 'You have a pending rent review request:',
  newRentAmount = '£1700',
  badgeText,
  documents = [],
  statusTagText = 'e-sign requested',
  infoMessage,
  rejectButtonText = 'Reject Offer',
  signButtonText = 'Sign Agreement',
  showButtons = true,
  showRejectButton = true,
  isEmpty = false,
  emptyMessage = "You don't have any active rent-review. Kindly contact your agent for more info.",
  onRejectOffer,
  onSignAgreement,
  onDownloadDoc,
  onContactAgent,
}) => {
  const themeObj = getTokens('light').colors;

  const handleDocClick = (doc: DocumentItem) => {
    if (onDownloadDoc) {
      onDownloadDoc(doc);
    } else if (doc.url) {
      if (Platform.OS === 'web') {
        window.open(doc.url, '_blank', 'noreferrer');
      } else {
        Linking.openURL(doc.url);
      }
    }
  };

  const getStatusBadgeStyle = (statusStr: string) => {
    const s = (statusStr || '').toLowerCase();
    if (s.includes('accept') || s.includes('complete') || s.includes('signed')) {
      return { bg: '#D1FAE5', color: '#059669' };
    }
    if (s.includes('reject')) {
      return { bg: '#FEE2E2', color: '#DC2626' };
    }
    return { bg: '#FEF08A', color: '#92400E' };
  };

  if (isEmpty) {
    return (
      <View style={[styles(themeObj).cardContainer, styles(themeObj).emptyContainer, restStyle]}>
        <View style={styles(themeObj).emptyIconCircle}>
          <ODSIcon icon="TB-Alert" size={32} color="#D97706" />
        </View>
        <ODSText type="h4" color="#101828" textAlign="center" style={{ fontWeight: '700' }}>
          No Active Rent Review
        </ODSText>
        <ODSText type="md" marginTop={20} color="#667085" textAlign="center" style={{ marginBottom: 28, lineHeight: 22 }}>
          {emptyMessage}
        </ODSText>
      </View>
    );
  }

  const badgeColors = getStatusBadgeStyle(statusTagText);
  const formattedStatusTagText = formatText(statusTagText);
  const formattedRejectButtonText = formatText(rejectButtonText);
  const formattedSignButtonText = formatText(signButtonText);

  return (
    <View style={[styles(themeObj).cardContainer, restStyle]}>
      {/* Header Row: Icon + Title + Avatar Badge */}
      <View style={styles(themeObj).headerRow}>
        <View style={styles(themeObj).headerLeft}>
          <View style={styles(themeObj).alertIconCircle}>
            <ODSIcon icon="TB-Alert" size={22} color="#EAAA08" />
          </View>
          <ODSText type="h5" color="#101828" style={{ fontWeight: '700', flex: 1, flexShrink: 1 }}>
            {title}
          </ODSText>
        </View>
        {badgeText && (
          <View style={styles(themeObj).avatarBadge}>
            <ODSText type="xs-bold" color="#FFFFFF">
              {badgeText}
            </ODSText>
          </View>
        )}
      </View>

      {/* Description */}
      <ODSText type="sm" color="#667085" style={{ marginBottom: 12 }}>
        {description}
      </ODSText>

      {/* New Rent Section */}
      <View style={styles(themeObj).rentSection}>
        <ODSText type="sm" color="#667085" style={{ fontWeight: '500' }}>
          New Rent
        </ODSText>
        <ODSText type="h4" color="#101828" style={{ fontWeight: '700', marginTop: 4 }}>
          {newRentAmount}
        </ODSText>
      </View>

      {/* Document List */}
      {documents && documents.length > 0 && (
        <View style={styles(themeObj).docListContainer}>
          {documents.map((doc, index) => (
            <TouchableOpacity
              key={doc.id || index}
              activeOpacity={0.7}
              onPress={() => handleDocClick(doc)}
              style={styles(themeObj).docCard}
            >
              {/* Download Icon on Left */}
              <View style={styles(themeObj).docLeftIcon}>
                <ODSIcon icon="TB-Download" size={22} color="#1FA6B2" />
              </View>

              {/* Title and Date in Center */}
              <View style={styles(themeObj).docTextContainer}>
                <ODSText type="sm-bold" color="#101828">
                  {doc.title}
                </ODSText>
                <ODSText type="xs" color="#667085" style={{ marginTop: 2 }}>
                  {doc.date}
                </ODSText>
              </View>

              {/* View / Eye / Download Icon on Right */}
              <View style={styles(themeObj).docRightStatus}>
                <ODSIcon
                  icon={doc.status?.toLowerCase() === 'download' ? 'TB-Download' : 'eye'}
                  size={20}
                  color={themeObj.main.blue[400]}
                />
                <ODSText type="xs" color="#667085" style={{ marginTop: 2 }}>
                  {doc.status || 'View'}
                </ODSText>
              </View>
            </TouchableOpacity>
          ))}
        </View>
      )}

      {/* Status Tag Badge */}
      {statusTagText && (
        <View style={styles(themeObj).statusBadgeContainer}>
          <View style={[styles(themeObj).statusBadge, { backgroundColor: badgeColors.bg }]}>
            <ODSText type="xs-bold" color={badgeColors.color}>
              {formattedStatusTagText}
            </ODSText>
          </View>
          {infoMessage ? (
            <ODSText type="xs" color="#667085" style={{ marginTop: 8, textAlign: 'center' }}>
              {infoMessage}
            </ODSText>
          ) : null}
        </View>
      )}

      {/* Action Buttons Row */}
      {showButtons && (
        <View style={styles(themeObj).actionButtonsRow}>
          {showRejectButton && (
            <View style={{ flex: 1, marginRight: 6 }}>
              <ODSButton
                type="primary"
                themeColor={themeObj.main?.red || '#F04438'}
                restStyle={{ borderRadius: 1000, paddingHorizontal: 8 }}
                txtSize={15}
                adjustsFontSizeToFit={true}
                numberOfLines={1}
                onPress={onRejectOffer}
              >
                {formattedRejectButtonText}
              </ODSButton>
            </View>
          )}

          <View style={{ flex: 1, marginLeft: showRejectButton ? 6 : 0 }}>
            <ODSButton
              type="primary"
              disabled={false}
              btnCOlor="#FFFFFF"
              restStyle={{ backgroundColor: '#1FA6B2', borderRadius: 1000, paddingHorizontal: 8 }}
              txtSize={15}
              adjustsFontSizeToFit={true}
              numberOfLines={1}
              onPress={onSignAgreement}
            >
              {formattedSignButtonText}
            </ODSButton>
          </View>
        </View>
      )}
    </View>
  );
};

const styles = (theme: any) =>
  StyleSheet.create({
    cardContainer: {
      width: '100%',
      maxWidth: 620,
      borderRadius: 20,
      backgroundColor: '#FFFFFF',
      paddingHorizontal: 20,
      paddingVertical: 24,
      shadowColor: '#101828',
      shadowOffset: { width: 0, height: 4 },
      shadowOpacity: 0.08,
      shadowRadius: 16,
      elevation: 4,
      borderWidth: 1,
      borderColor: '#E4E7EC',
      alignSelf: 'center',
    },
    emptyContainer: {
      alignItems: 'center',
      paddingVertical: 48,
      paddingHorizontal: 24,
    },
    emptyIconCircle: {
      width: 56,
      height: 56,
      borderRadius: 28,
      backgroundColor: '#FEF3C7',
      justifyContent: 'center',
      alignItems: 'center',
      marginBottom: 20,
    },
    headerRow: {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'space-between',
      marginBottom: 8,
    },
    headerLeft: {
      flexDirection: 'row',
      alignItems: 'center',
      flex: 1,
      marginRight: 8,
    },
    alertIconCircle: {
      width: 36,
      height: 36,
      borderRadius: 18,
      justifyContent: 'center',
      alignItems: 'center',
      marginRight: 12,
    },
    avatarBadge: {
      width: 32,
      height: 32,
      borderRadius: 16,
      backgroundColor: '#00A651',
      justifyContent: 'center',
      alignItems: 'center',
    },
    rentSection: {
      marginBottom: 24,
      marginTop: 20,
    },
    docListContainer: {
      marginBottom: 20,
    },
    docCard: {
      flexDirection: 'row',
      alignItems: 'center',
      padding: 16,
      borderRadius: 14,
      borderWidth: 1,
      borderColor: '#EAECF0',
      backgroundColor: '#FFFFFF',
      marginBottom: 12,
    },
    docLeftIcon: {
      marginRight: 16,
    },
    docTextContainer: {
      flex: 1,
    },
    docRightStatus: {
      alignItems: 'center',
      justifyContent: 'center',
      marginLeft: 12,
    },
    statusBadgeContainer: {
      alignItems: 'center',
      marginBottom: 24,
    },
    statusBadge: {
      paddingVertical: 6,
      paddingHorizontal: 16,
      borderRadius: 8,
    },
    actionButtonsRow: {
      flexDirection: 'row',
      alignItems: 'center',
    },
  });

export default RentReviewDetailCard;
